
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DollarSign, Scan, Search, X } from "lucide-react";
import { BarcodeScanner } from "../BarcodeScanner";
import { useIsMobile } from "@/hooks/use-mobile";

interface SearchSectionProps {
  barcode: string;
  setBarcode: (value: string) => void;
  onSearch: () => void;
  onClearSearch: () => void;
  isLoading: boolean;
}

export const SearchSection = ({ 
  barcode, 
  setBarcode, 
  onSearch, 
  onClearSearch, 
  isLoading 
}: SearchSectionProps) => {
  const [showScanner, setShowScanner] = useState(false);
  const isMobile = useIsMobile();
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus input on component mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleScanResult = (scannedBarcode: string) => {
    setBarcode(scannedBarcode);
    setShowScanner(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && barcode.trim()) {
      onSearch();
    }
  };

  return (
    <>
      <Card className="mb-8 shadow-lg border-0 bg-card/50 backdrop-blur-sm">
        <CardHeader className="text-center pb-6">
          <CardTitle className="flex items-center justify-center gap-3 text-2xl md:text-3xl">
            <DollarSign className="w-8 h-8 text-primary" />
            Scan Barcode
          </CardTitle>
          <p className="text-lg text-muted-foreground mt-2">
            Scan barcode with hardware scanner or enter manually
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            <div className="relative">
              <Input
                ref={inputRef}
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Scan barcode or type manually..."
                className="text-center text-2xl h-16 pr-12 font-mono tracking-wider"
                disabled={isLoading}
              />
              {barcode && (
                <Button
                  onClick={onClearSearch}
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button 
                onClick={() => setShowScanner(true)} 
                variant="outline"
                size="lg"
                className="flex items-center gap-2 px-8 py-3 text-lg"
              >
                <Scan className="w-5 h-5" />
                Camera Scanner
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {showScanner && (
        <BarcodeScanner
          onScanResult={handleScanResult}
          onClose={() => setShowScanner(false)}
        />
      )}
    </>
  );
};
